import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { LogOut, User, Mail, CreditCard } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { getDomainMeta } from '../utils/workItemStyling';

import type { BootstrapResponse } from '../api/bootstrap';

type MailboxAggregate = BootstrapResponse['mailbox'];

/**
 * Shell aggregate dot: separate syncing vs connection vs sync-failure signals (FR4).
 * Healthy connected state returns null so the dot can use the active domain color.
 */
const statusDotClass = (mailbox?: MailboxAggregate | null): string | null => {
    if (!mailbox || !mailbox.connected) return 'bg-zinc-500';
    if (mailbox.any_syncing) return 'bg-amber-400 animate-pulse';
    if (mailbox.any_connection_errors) return 'bg-rose-500';
    if (mailbox.any_sync_failures) return 'bg-orange-400';
    if (mailbox.any_errors) return 'bg-rose-400';
    return null;
};

function isNodeInside(root: HTMLElement | null, node: EventTarget | null): boolean {
    return Boolean(root && node instanceof Node && root.contains(node));
}

function isForeignOverlayTarget(target: EventTarget | null, root: HTMLElement | null): boolean {
    if (!(target instanceof Element) || !root || root.contains(target)) return false;
    return Boolean(
        target.closest('[role="dialog"], [aria-modal="true"], .MuiModal-root, #wb-drawer-panel'),
    );
}

const mailboxStatusSummary = (mailbox: MailboxAggregate | undefined | null): string => {
    if (!mailbox || !mailbox.connected) return 'Mailboxes: not connected';
    const parts: string[] = [];
    if (mailbox.any_syncing) parts.push('Sync in progress');
    if (mailbox.any_connection_errors) parts.push('Connection issue');
    if (mailbox.any_sync_failures) parts.push('Sync issue');
    if (parts.length === 0 && mailbox.any_errors) parts.push('Mailbox issue');
    return parts.length > 0 ? parts.join(' · ') : 'Mailboxes: healthy';
};

const AccountChip: React.FC = () => {
    const { session, signOut } = useAuth();
    const { bootstrap } = useAppBootstrap();
    const navigate = useNavigate();
    // Below 1024 px the dot is cut: only its hover title said what it meant (phone-top-bar).
    const showStatusDot = useMediaQuery('(min-width: 1024px)', { noSsr: true });
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: PointerEvent) => {
            if (!isNodeInside(rootRef.current, event.target)) {
                setOpen(false);
            }
        };

        const onFocusIn = (event: FocusEvent) => {
            if (isForeignOverlayTarget(event.target, rootRef.current)) {
                setOpen(false);
            }
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            // A drawer/dialog above this popover must receive Escape. Close ourselves
            // without capturing the event so MUI onClose still runs.
            if (!isNodeInside(rootRef.current, event.target)) {
                setOpen(false);
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open]);

    const identity = useMemo(() => {
        const email = bootstrap?.user?.email || session?.user?.email || '';
        const displayName = bootstrap?.user?.display_name || email?.split('@')[0] || '';
        const initial = (displayName || email || '?').slice(0, 1).toUpperCase();
        const domainLabel = bootstrap?.active_domain_config?.label || 'Job Hunt';
        return { email, displayName, initial, domainLabel };
    }, [bootstrap, session]);

    const domainDotColor = useMemo(() => {
        const activeKey = (
            bootstrap?.active_domain_key
            || bootstrap?.active_domain_config?.key
            || 'general'
        ).toString();

        const activeConfig = bootstrap?.active_domain_config;
        const configOverride = activeConfig?.key
            ? [{
                key: String(activeConfig.key),
                label: activeConfig.label ?? null,
                icon: activeConfig.icon ?? null,
                color: activeConfig.color ?? null,
            }]
            : undefined;

        return getDomainMeta(activeKey, configOverride).color;
    }, [bootstrap?.active_domain_config, bootstrap?.active_domain_key]);

    const handleLogout = async () => {
        await signOut();
        setOpen(false);
        navigate('/login', { replace: true });
    };

    if (!session) return null;

    const mb = bootstrap?.mailbox;
    const syncDotClass = statusDotClass(mb);
    const dotStyle = syncDotClass ? undefined : { backgroundColor: domainDotColor };
    const dotTitle = mailboxStatusSummary(mb ?? null);

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                ref={triggerRef}
                data-testid="ACCOUNT_CHIP"
                title={showStatusDot ? dotTitle : undefined}
                aria-label="Account menu"
                aria-expanded={open}
                aria-controls={open ? 'account-chip-menu' : undefined}
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-theme bg-theme-card px-2.5 py-2 text-sm text-theme hover:bg-theme-card-hover transition max-sm:px-2 max-sm:py-1.5"
            >
                {showStatusDot && (
                    <span
                        data-testid="ACCOUNT_CHIP_STATUS_DOT"
                        className={`h-2 w-2 rounded-full ${syncDotClass || ''}`}
                        style={dotStyle}
                        aria-hidden
                    />
                )}
                <div className="h-8 w-8 rounded-full bg-theme-tertiary flex items-center justify-center text-sm font-semibold shrink-0">
                    {identity.initial}
                </div>
                <div className="hidden sm:flex flex-col items-start leading-tight text-left min-w-[140px] max-w-[180px]">
                    <span className="text-sm font-semibold text-theme">{identity.displayName || 'Account'}</span>
                    <span className="text-[11px] text-theme-secondary truncate max-w-[160px]">{identity.email}</span>
                </div>
                <span className="hidden md:inline text-[11px] text-theme-secondary ml-1 truncate max-w-[100px]">{identity.domainLabel}</span>
            </button>

            {open && (
                <div
                    id="account-chip-menu"
                    data-testid="ACCOUNT_CHIP_MENU"
                    className="absolute right-0 mt-2 w-64 max-sm:w-56 rounded-2xl border border-theme glass-card shadow-xl backdrop-blur z-50 animate-slide-down-fade origin-top-right"
                >
                    <div className="px-4 py-3 border-b border-theme">
                        <div className="text-sm font-semibold text-theme">{identity.displayName || 'Account'}</div>
                        <div className="text-xs text-theme-secondary truncate">{identity.email}</div>
                        <div className="text-[11px] text-theme-muted mt-1">Mode: {identity.domainLabel}</div>
                        {bootstrap?.mailbox?.connected && (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-theme-muted mt-1">
                                <span className="inline-flex items-center gap-1">
                                    <Mail className="h-3 w-3 shrink-0" />
                                    {bootstrap.mailbox.mailbox_count} mailbox{bootstrap.mailbox.mailbox_count !== 1 ? 'es' : ''}
                                </span>
                                {bootstrap.mailbox.any_syncing && (
                                    <span className="text-amber-400">Sync: syncing</span>
                                )}
                                {bootstrap.mailbox.any_connection_errors && (
                                    <span className="text-rose-400">Conn: issue</span>
                                )}
                                {bootstrap.mailbox.any_sync_failures && (
                                    <span className="text-orange-400">Sync: issue</span>
                                )}
                                {bootstrap.mailbox.any_errors &&
                                    !bootstrap.mailbox.any_sync_failures &&
                                    !bootstrap.mailbox.any_connection_errors && (
                                        <span className="text-rose-400">Issue</span>
                                    )}
                            </div>
                        )}
                    </div>
                    <div className="py-1">
                        <button
                            type="button"
                            data-testid="ACCOUNT_CHIP_PROFILE"
                            onClick={() => {
                                setOpen(false);
                                navigate('/app/profile');
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-theme hover:bg-theme-card-hover flex items-center gap-2"
                        >
                            <User className="h-4 w-4 text-theme-secondary" />
                            Profile
                        </button>
                        <button
                            type="button"
                            data-testid="ACCOUNT_CHIP_BILLING"
                            onClick={() => {
                                setOpen(false);
                                navigate('/app/billing');
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-theme hover:bg-theme-card-hover flex items-center gap-2"
                        >
                            <CreditCard className="h-4 w-4 text-theme-secondary" />
                            Billing
                        </button>
                        <button
                            type="button"
                            data-testid="ACCOUNT_CHIP_LOGOUT"
                            onClick={handleLogout}
                            className="w-full px-4 py-2 text-left text-sm text-rose-500 dark:text-rose-300 hover:bg-rose-500/10 flex items-center gap-2"
                        >
                            <LogOut className="h-4 w-4" />
                            Logout
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountChip;
